import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { ReactComponent as IconPhone } from 'images/phone.svg';
import { ReactComponent as IconSearch } from 'images/search.svg';
import {
  isValidPhoneNumber,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
} from 'react-phone-number-input';
import { CountryCode } from 'libphonenumber-js';
import ReactPhoneInput from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en.json'
import flags from 'react-phone-number-input/flags'
import 'react-phone-number-input/style.css';
import Dropdown from 'rc-dropdown';
import useLocation from 'sdk/hooks/useLocation';
import 'rc-dropdown/assets/index.css';
import Input from './Input';
import styles from './Input.module.scss';
import phoneStyles from './PhoneInput.module.scss';

type CountryItemProps = {
  country: CountryObj,
  onChange: (value: CountryCode) => void,
}

type CountryObj = {
  country: CountryCode,
  code: string,
  name: string,
}

type PhoneInputProps = {
  className?: string,
  onChange: (value: string, isValid: boolean) => void,
  value?: string,
  disabled?: boolean,
  error?: boolean,
  errorMessage?: string,
  onBlur?: () => void,
  white?: boolean,
  wide?: boolean,
  placeholder?: string
  onKeyDown?: (e: any) => void;
}

type CountrySelectProps = {
  value: CountryCode | undefined,
  onChange: (value: CountryCode | undefined) => void,
  white: boolean,
}

function CountryItem({
  country: {
    country,
    name,
    code,
  },
  onChange,
}: CountryItemProps) {
  const F = flags[country]
  return (
    <div
      key={country}
      className={phoneStyles.optionLabel}
      onClick={() => {
        if (onChange) onChange(country || undefined)
      }}
    >
      {F ? (
        <div className={phoneStyles.dropdownIcon}>
          <F title="" />
        </div>
      ) : null}
      <span className={phoneStyles.optionName}>
        {name}
      </span>
      <span className={phoneStyles.optionCode}>
        +
        {code}
      </span>
    </div>
  )
}

function CountrySelect({
  value,
  onChange,
  white = false,
}: CountrySelectProps) {
  const [search, setSearch] = useState<string>('')

  const countries = useMemo(() => {
    const topCountries: CountryCode[] = ['US', 'CA']
    const restCountries: CountryCode[] = getCountries()
      .filter((c: CountryCode) => !topCountries.includes(c))

    return [...topCountries, ...restCountries].map((c) => ({
      code: getCountryCallingCode(c),
      country: c,
      name: en[c],
    }))
  }, [])

  const filteredCountries = useMemo(() => {
    if (search[0] === '+') {
      return countries.filter((c) => (c.code.includes(search.substr(1))))
    }
    if (/^\d+$/.test(search)) {
      return countries.filter((c) => c.code.includes(search))
    }
    const searchStr = search.toLowerCase()
    return countries.filter(({ country, name }) => (
      (country.toLowerCase().includes(searchStr))
      || (name.toLowerCase().includes(searchStr))
    ))
  }, [search, countries])

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearch((e.target as HTMLInputElement).value)
  }

  return (
    <Dropdown
      overlayClassName={phoneStyles.dropdown}
      trigger={['click']}
      overlay={(
        <div className={clsx(phoneStyles.dropdownMenu, white && phoneStyles.dropdownMenuWhite)}>
          <div
            className={clsx(
              styles.container,
              phoneStyles.dropdownInput,
              (search === '') && styles.containerEmpty,
            )}
          >
            <Input
              icon={<IconSearch />}
              className={styles.input}
              onClick={(e) => {
                e?.stopPropagation()
              }}
              onChange={handleSearchChange}
              value={search}
              placeholder="Search"
              wide
              white={white}
            />
          </div>
          <div className={phoneStyles.dropdownMenuInner}>
            {filteredCountries.map((country: CountryObj) => (
              <CountryItem
                key={country.country}
                country={country}
                onChange={(v) => {
                  setSearch('')
                  onChange(v)
                }}
              />
            ))}
          </div>
        </div>
      )}
      openClassName={phoneStyles.dropdownOpen}
    >
      <div className={phoneStyles.dropdownControl}>
        <span className={phoneStyles.optionShortName}>
          {value}
        </span>
        <span className={phoneStyles.optionCode}>
          {' +'}
          {value && getCountryCallingCode(value)}
        </span>
        <span className={clsx(phoneStyles.dropdownArrow, phoneStyles.dropdownArrowOpen)} />
      </div>
    </Dropdown>
  )
}

const InputComponent = React.forwardRef((props: any, ref) => {
  const valueDigits = (props.value || '').replace(/\D/g, '')
  const maxLength = (12 - valueDigits.length) + props.value.length
  return (
    <input
      ref={ref}
      minLength={7}
      maxLength={maxLength}
      {...props}
    />
  )
})

function PhoneInput({
  className = '',
  onChange = () => {},
  value = '',
  disabled = false,
  error = false,
  errorMessage = '',
  white = false,
  wide = false,
  onKeyDown,
  ...restInputProps
}: PhoneInputProps) {
  const [country, setCountry] = useState<CountryCode | undefined>()
  const locationData = useLocation()
  const countryCode = locationData?.data?.location?.country_code

  useEffect(() => {
    if (value) {
      const phoneData = parsePhoneNumber(value)
      if (phoneData?.country) {
        setCountry(phoneData?.country)
      }
    }
  }, [value])

  useEffect(() => {
    if (countryCode && !value && (!country)) {
      setCountry(countryCode)
    }
  }, [countryCode])

  const valueStr = ((value || '').substr(0, 1) === '+') ? value : `+${value}`

  return (
    <>
      <div
        className={clsx(
          styles.container,
          wide && styles.containerWide,
          disabled && styles.containerDisabled,
          error && styles.error,
          white && styles.containerWhite,
        )}
        onKeyDown={onKeyDown}
      >
        <IconPhone className={styles.prefix} />
        <div className={phoneStyles.countrySelect}>
          <CountrySelect
            value={country || 'US'}
            onChange={(v) => setCountry(v)}
            white={white}
          />
        </div>

        <ReactPhoneInput
          key={country}
          value={valueStr}
          onChange={(v) => onChange(v || '', v ? isValidPhoneNumber(v) : false)}
          className={clsx(styles.input, className, white && styles.inputWhite)}
          flags={flags}
          disabled={disabled}
          country={country || 'US'}
          withCountryCallingCode={false}
          // @ts-ignore
          inputComponent={InputComponent}
          {...restInputProps}
        />
      </div>
      {(error && errorMessage) && (
        <div className={styles.errorMessage}>{errorMessage}</div>
      )}
    </>
  )
}

export default PhoneInput
